import type { Permiso, RespuestaLogin, UsuarioSesion } from '@barbones/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, configurarSesion, restaurarSesion } from './cliente';
import { vaciarCola } from './offline/cola';

type Estado =
  | { tipo: 'cargando' }
  | { tipo: 'anonimo' }
  | { tipo: 'autenticado'; usuario: UsuarioSesion };

interface ContextoSesion {
  estado: Estado;
  usuario: UsuarioSesion | null;
  sucursalId: string | null;
  puede: (permiso: Permiso) => boolean;
  iniciarSesion: (usuario: string, password: string) => Promise<void>;
  cerrarSesion: () => Promise<void>;
  cambiarSucursal: (id: string) => void;
}

const Contexto = createContext<ContextoSesion | null>(null);
const CLAVE_SUCURSAL = 'bb-sucursal';
/** Último usuario (sin secretos) para abrir la app sin red; el token nunca se guarda. */
const CLAVE_USUARIO = 'bb-usuario-offline';

function recordarUsuario(usuario: UsuarioSesion | null): void {
  try {
    if (usuario) localStorage.setItem(CLAVE_USUARIO, JSON.stringify(usuario));
    else localStorage.removeItem(CLAVE_USUARIO);
  } catch {
    /* sin almacenamiento: simplemente no habrá modo sin conexión */
  }
}

function usuarioRecordado(): UsuarioSesion | null {
  try {
    const texto = localStorage.getItem(CLAVE_USUARIO);
    return texto ? (JSON.parse(texto) as UsuarioSesion) : null;
  } catch {
    return null;
  }
}

function leerSucursalGuardada(): string | null {
  try {
    return localStorage.getItem(CLAVE_SUCURSAL);
  } catch {
    return null;
  }
}

export function SesionProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ tipo: 'cargando' });
  const [sucursalId, setSucursalId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const aplicar = useCallback((datos: RespuestaLogin) => {
    const guardada = leerSucursalGuardada();
    const sucursal = guardada && datos.usuario.sucursalIds.includes(guardada) ? guardada : (datos.usuario.sucursalIds[0] ?? null);
    configurarSesion({ token: datos.accessToken, sucursalId: sucursal, usuarioId: datos.usuario.id });
    setSucursalId(sucursal);
    setEstado({ tipo: 'autenticado', usuario: datos.usuario });
    recordarUsuario(datos.usuario);
  }, []);

  useEffect(() => {
    configurarSesion({
      onRenovado: aplicar,
      onExpirada: () => {
        configurarSesion({ token: null });
        recordarUsuario(null);
        setEstado({ tipo: 'anonimo' });
      },
    });

    // Sin red al abrir: se entra con el último usuario para consultar la caché y capturar en la cola;
    // al volver la red se valida la sesión con el servidor (si ya no es válida, se pide login).
    const recordado = usuarioRecordado();
    if (!navigator.onLine && recordado) {
      const guardada = leerSucursalGuardada();
      const sucursal = guardada && recordado.sucursalIds.includes(guardada) ? guardada : (recordado.sucursalIds[0] ?? null);
      configurarSesion({ token: null, sucursalId: sucursal, usuarioId: recordado.id });
      setSucursalId(sucursal);
      setEstado({ tipo: 'autenticado', usuario: recordado });
      const alVolver = () => {
        void restaurarSesion().then((datos) => {
          if (!datos) {
            recordarUsuario(null);
            setEstado({ tipo: 'anonimo' });
          }
        });
      };
      window.addEventListener('online', alVolver, { once: true });
      return () => window.removeEventListener('online', alVolver);
    }

    // Restaura la sesión a partir de la cookie de refresh.
    void restaurarSesion().then((datos) => {
      if (!datos) setEstado({ tipo: 'anonimo' });
    });
    return undefined;
  }, [aplicar]);

  const iniciarSesion = useCallback(
    async (usuario: string, password: string) => {
      const datos = await api<RespuestaLogin>('/auth/login', { method: 'POST', body: { usuario, password, cliente: 'web' } });
      aplicar(datos);
    },
    [aplicar],
  );

  const cerrarSesion = useCallback(async () => {
    await api('/auth/logout', { method: 'POST', body: {} }).catch(() => undefined);
    // Equipo compartido: lo pendiente de este usuario no se envía con la sesión de otro.
    await vaciarCola();
    recordarUsuario(null);
    configurarSesion({ token: null, sucursalId: null, usuarioId: null });
    queryClient.clear();
    setEstado({ tipo: 'anonimo' });
  }, [queryClient]);

  const cambiarSucursal = useCallback(
    (id: string) => {
      configurarSesion({ sucursalId: id });
      setSucursalId(id);
      try {
        localStorage.setItem(CLAVE_SUCURSAL, id);
      } catch {
        /* almacenamiento no disponible: solo afecta la preferencia */
      }
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const valor = useMemo<ContextoSesion>(() => {
    const usuario = estado.tipo === 'autenticado' ? estado.usuario : null;
    return {
      estado,
      usuario,
      sucursalId,
      puede: (permiso) => usuario?.permisos.includes(permiso) ?? false,
      iniciarSesion,
      cerrarSesion,
      cambiarSucursal,
    };
  }, [estado, sucursalId, iniciarSesion, cerrarSesion, cambiarSucursal]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): ContextoSesion {
  const ctx = useContext(Contexto);
  if (!ctx) throw new Error('useSesion debe usarse dentro de <SesionProvider>');
  return ctx;
}

/** Usuario autenticado (solo dentro de rutas protegidas). */
export function useUsuario(): UsuarioSesion {
  const { usuario } = useSesion();
  if (!usuario) throw new Error('useUsuario requiere una sesión activa');
  return usuario;
}
